import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuthStore, useUIStore } from '@/stores';
import { useIsMobile } from '@/hooks';
import { auth } from '@/lib/supabase';
import { Avatar } from '@/components/ui';
import {
  LayoutDashboard,
  Users,
  UtensilsCrossed,
  Apple,
  FileText,
  Calendar,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
} from 'lucide-react';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { path: '/app/dashboard', label: 'Il mio studio', icon: <LayoutDashboard size={20} /> },
  { path: '/app/patients', label: 'Pazienti', icon: <Users size={20} /> },
  { path: '/app/diets', label: 'Piani alimentari', icon: <UtensilsCrossed size={20} /> },
  { path: '/app/foods', label: 'Alimenti', icon: <Apple size={20} /> },
  { path: '/app/reports', label: 'Report', icon: <FileText size={20} /> },
  { path: '/app/appointments', label: 'Agenda', icon: <Calendar size={20} /> },
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useUIStore();
  const isMobile = useIsMobile(1024);
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [isMobile, location.pathname, setSidebarOpen]);

  const handleLogout = async () => {
    await auth.signOut();
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        inert={isMobile && !sidebarOpen}
        className={cn(
          'fixed top-0 left-0 z-50 h-full bg-white border-r border-gray-200 transition-all duration-300',
          'lg:static lg:z-auto',
          sidebarOpen ? 'w-64 translate-x-0' : 'w-0 -translate-x-full lg:w-20 lg:translate-x-0'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-[72px] px-5 border-b border-gray-200">
            {sidebarOpen ? (
              <>
                <Link to="/app/dashboard" className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-teal-600 rounded-xl flex items-center justify-center shadow-sm">
                    <span className="text-white font-bold text-sm tracking-tight">N.</span>
                  </div>
                  <div><span className="font-semibold tracking-[.16em] text-gray-900">NUTRY</span><span className="block text-[10px] uppercase tracking-[.12em] text-gray-500">Studio</span></div>
                </Link>
                <button
                  aria-label="Chiudi navigazione"
                  onClick={toggleSidebar}
                  className="p-1 text-gray-500 hover:text-gray-700 lg:hidden"
                >
                  <X size={20} />
                </button>
              </>
            ) : (
              <div className="w-full flex justify-center">
                <div className="w-9 h-9 bg-teal-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-sm">N.</span>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav aria-label="Navigazione principale" className="flex-1 overflow-y-auto py-5 px-3">
            <ul className="space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      aria-current={isActive ? 'page' : undefined}
                      aria-label={item.label}
                      title={item.label}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors',
                        isActive
                          ? 'bg-teal-50 text-teal-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      )}
                    >
                      {item.icon}
                      {sidebarOpen && <span className="font-medium">{item.label}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User section */}
          <div className="border-t border-gray-200 p-4">
            {sidebarOpen ? (
              <div className="flex items-center gap-3 mb-4">
                <Avatar
                  fallback={user?.first_name && user?.last_name ? `${user.first_name[0]}${user.last_name[0]}` : user?.email?.[0]?.toUpperCase() || 'U'}
                  size="md"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : 'Utente'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              </div>
            ) : null}

            <div className={cn('space-y-1', !sidebarOpen && 'flex flex-col items-center')}>
              <Link
                to="/app/settings"
                aria-label="Impostazioni"
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors',
                  !sidebarOpen && 'justify-center'
                )}
              >
                <Settings size={20} />
                {sidebarOpen && <span className="text-sm">Impostazioni</span>}
              </Link>
              <button
                aria-label="Esci"
                onClick={handleLogout}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors',
                  !sidebarOpen && 'justify-center'
                )}
              >
                <LogOut size={20} />
                {sidebarOpen && <span className="text-sm">Esci</span>}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export const AppLayout: React.FC = () => {
  const { sidebarOpen, toggleSidebar } = useUIStore();
  const location = useLocation();
  const currentSection = navItems.find(item => location.pathname.startsWith(item.path))?.label || (location.pathname.startsWith('/app/settings') ? 'Impostazioni' : 'Il mio studio');

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="min-w-0 flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-[72px] shrink-0 bg-white/90 border-b border-gray-200 flex items-center px-4 lg:px-7">
          <button
            aria-label={sidebarOpen ? 'Comprimi navigazione' : 'Apri navigazione'}
            aria-expanded={sidebarOpen}
            onClick={toggleSidebar}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            {sidebarOpen ? <ChevronLeft size={20} /> : <Menu size={20} />}
          </button>
          <div className="ml-3 flex flex-1 items-center justify-between gap-3"><span className="text-sm font-medium text-gray-900">{currentSection}</span><span className="rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-teal-700">NUTRY Studio</span></div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:px-8 lg:py-7">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

// Page Header Component
interface PageHeaderProps {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  backLink?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, action, backLink }) => {
  const navigate = useNavigate();

  return (
    <div className="mb-6">
      {backLink && (
        <button
          onClick={() => navigate(backLink)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
        >
          <ChevronLeft size={16} />
          Indietro
        </button>
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
        </div>
        {action}
      </div>
    </div>
  );
};
